import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import type { UserProfile } from '../../types';

interface AdminOverviewProps {
  onNavigateTab: (tab: 'overview' | 'users') => void;
  onOpenCreateUserModal?: () => void;
  onBackToPortal: () => void;
}

export default function AdminOverview({ onNavigateTab, onOpenCreateUserModal, onBackToPortal }: AdminOverviewProps) {
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [quotationCount, setQuotationCount] = useState<number>(0);
  const [partyCount, setPartyCount] = useState<number>(0);
  const [itemCount, setItemCount] = useState<number>(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadOverviewData();
  }, []);

  const loadOverviewData = async () => {
    setLoading(true);
    try {
      // 1. Fetch user profiles
      const { data: userData } = await supabase
        .from('user_profiles')
        .select('*')
        .order('created_at', { ascending: false });
      if (userData) setUsers(userData);

      // 2. Fetch counts
      const [qRes, pRes, iRes] = await Promise.all([
        supabase.from('quotations').select('id', { count: 'exact', head: true }),
        supabase.from('parties').select('id', { count: 'exact', head: true }),
        supabase.from('item_codes').select('id', { count: 'exact', head: true }),
      ]);

      if (qRes.count != null) setQuotationCount(qRes.count);
      if (pRes.count != null) setPartyCount(pRes.count);
      if (iRes.count != null) setItemCount(iRes.count);
    } catch (err) {
      console.warn('Overview data load warning:', err);
    } finally {
      setLoading(false);
    }
  };

  const activeUsersCount = users.filter(u => u.is_active !== false).length;
  const adminCount = users.filter(u => u.role === 'admin' || u.role === 'executive').length;

  return (
    <div className="space-y-6">
      {/* Top Banner */}
      <div className="rounded-2xl bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 p-6 sm:p-8 text-white shadow-xl border border-indigo-500/20 relative overflow-hidden">
        <div className="absolute -right-10 -bottom-10 w-64 h-64 bg-indigo-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 relative z-10">
          <div>
            <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-amber-400/10 border border-amber-400/30 text-amber-300 text-xs font-semibold mb-2">
              <span className="h-2 w-2 rounded-full bg-amber-400 animate-pulse"></span>
              ADMINISTRATIVE COMMAND · /admin
            </div>
            <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight">System & User Governance</h1>
            <p className="text-slate-300 text-sm mt-1 max-w-2xl">
              Centralized administrative command for user management, role assignments, and organizational oversight.
            </p>
          </div>
          <div className="flex flex-wrap gap-2.5">
            <button
              onClick={() => {
                onNavigateTab('users');
                if (onOpenCreateUserModal) onOpenCreateUserModal();
              }}
              className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold shadow-md transition-all flex items-center gap-2 cursor-pointer"
            >
              <span>➕</span> Add New User
            </button>
            <button
              onClick={onBackToPortal}
              className="px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white text-sm font-medium border border-white/20 transition-all flex items-center gap-2 cursor-pointer"
            >
              <span>←</span> Main Portal
            </button>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Total Staff Accounts</p>
            <h3 className="text-2xl font-black text-slate-900 mt-1">{loading ? '...' : users.length}</h3>
            <p className="text-xs text-emerald-600 font-medium mt-1">
              ✓ {activeUsersCount} Active Accounts
            </p>
          </div>
          <div className="h-12 w-12 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center text-xl font-bold">
            👥
          </div>
        </div>

        <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Customer Parties</p>
            <h3 className="text-2xl font-black text-slate-900 mt-1">{loading ? '...' : partyCount}</h3>
            <p className="text-xs text-indigo-600 font-medium mt-1">
              Registered Clients
            </p>
          </div>
          <div className="h-12 w-12 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center text-xl font-bold">
            🏢
          </div>
        </div>

        <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Catalog Items</p>
            <h3 className="text-2xl font-black text-slate-900 mt-1">{loading ? '...' : itemCount}</h3>
            <p className="text-xs text-amber-600 font-medium mt-1">
              Master Products
            </p>
          </div>
          <div className="h-12 w-12 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center text-xl font-bold">
            ⚙️
          </div>
        </div>

        <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Total Quotations</p>
            <h3 className="text-2xl font-black text-slate-900 mt-1">{loading ? '...' : quotationCount}</h3>
            <p className="text-xs text-purple-600 font-medium mt-1">
              Issued Records
            </p>
          </div>
          <div className="h-12 w-12 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center text-xl font-bold">
            📄
          </div>
        </div>
      </div>

      {/* Staff Directory Summary Card */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden flex flex-col">
        <div className="p-4 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-lg">👥</span>
            <h3 className="font-bold text-slate-900 text-sm">Staff Directory Summary</h3>
          </div>
          <button
            onClick={() => onNavigateTab('users')}
            className="text-xs font-semibold text-blue-600 hover:text-blue-800 cursor-pointer"
          >
            Manage Users & Roles →
          </button>
        </div>

        <div className="p-5 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex items-center justify-between p-4 rounded-xl bg-slate-50 border border-slate-100">
            <div className="flex items-center gap-3">
              <span className="p-2.5 rounded-lg bg-purple-100 text-purple-700 text-base font-bold">🛡️</span>
              <div>
                <h4 className="text-xs font-bold text-slate-800">Administrators & Directors</h4>
                <p className="text-[11px] text-slate-500">System governance & user management</p>
              </div>
            </div>
            <span className="font-bold text-sm text-purple-700 bg-purple-50 px-3 py-1 rounded-full border border-purple-200">
              {adminCount}
            </span>
          </div>

          <div className="flex items-center justify-between p-4 rounded-xl bg-slate-50 border border-slate-100">
            <div className="flex items-center gap-3">
              <span className="p-2.5 rounded-lg bg-blue-100 text-blue-700 text-base font-bold">💼</span>
              <div>
                <h4 className="text-xs font-bold text-slate-800">Sales Representatives & Managers</h4>
                <p className="text-[11px] text-slate-500">Quotation creation, pricing, client parties</p>
              </div>
            </div>
            <span className="font-bold text-sm text-blue-700 bg-blue-50 px-3 py-1 rounded-full border border-blue-200">
              {users.filter(u => u.role === 'sales_rep' || u.role === 'manager').length}
            </span>
          </div>

          <div className="flex items-center justify-between p-4 rounded-xl bg-slate-50 border border-slate-100">
            <div className="flex items-center gap-3">
              <span className="p-2.5 rounded-lg bg-emerald-100 text-emerald-700 text-base font-bold">📊</span>
              <div>
                <h4 className="text-xs font-bold text-slate-800">Finance & Accounts</h4>
                <p className="text-[11px] text-slate-500">Billing, accounts, financial reports</p>
              </div>
            </div>
            <span className="font-bold text-sm text-emerald-700 bg-emerald-50 px-3 py-1 rounded-full border border-emerald-200">
              {users.filter(u => u.role === 'accountant').length}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
